import { useState } from 'react'

import { Icon, Spinner, SquaredSwatch, Text } from '@joor/design-system'
import styled from 'styled-components'

import { formatNameWithCode } from 'utils/formatters'
import { encodeURIComponentImageName, getSizedImageUrl } from 'utils/images'

import { useStyleColorProps } from './styleColor.hooks'
import { GALLERY_IMAGE_DIMENSION } from 'features/Linesheets/Modals/ManageImagesModal/manageImagesModal.constants'
import { StyleColorsIds } from 'features/Linesheets/Modals/ManageImagesModal/manageImagesModal.ids'
import { StyleColor as StyleColorType } from 'features/Linesheets/Modals/ManageImagesModal/manageImagesModal.types'

const StyleColorContainer = styled.div`
  max-width: 364px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: auto;
  padding: 10px 0;

  & > section {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`

const AppendImagePlaceholder = styled.div`
  width: 52px;
  height: 62px;
  background-color: var(--color-secondary-100);
  border: 1px dashed var(--color-primary-200);
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;

  &:not(.disabled) {
    cursor: pointer;

    &:hover span svg {
      --main-color: var(--color-secondary-600);
    }
  }

  &.disabled {
    cursor: not-allowed;

    span svg {
      --main-color: var(--color-primary-200);

      &:hover {
        --main-color: var(--color-primary-200);
      }
    }
  }
`

const ImageContainer = styled.span`
  position: relative;

  &:hover span {
    opacity: 1;
    z-index: 9999;
  }
`

const ColorImage = styled.img`
  width: 52px;
  height: 62px;
  border: 1px dashed var(--color-primary-200);
  border-radius: 4px;
  object-fit: cover;
`

const Loader = styled(Spinner)`
  position: absolute;
  top: 40%;
  left: 35%;

  & circle {
    stroke: var(--color-secondary-600);
  }
`

const RemoveIcon = styled(Icon)`
  position: absolute;
  top: -8%;
  right: -8%;
  cursor: pointer;
  opacity: 0;
  z-index: -9999;

  svg {
    background-color: white;
    border-radius: 50%;
  }

  &.disabled svg {
    border: 1px solid var(--color-primary-200);
  }

  &:not(.disabled) svg {
    border: 1px solid var(--color-primary-800);
    &:hover {
      --main-color: var(--color-error-500);
      border: 1px solid var(--color-error-500);
    }
  }
`

type StyleColorProps = {
  color: StyleColorType
}

const StyleColor = ({ color }: StyleColorProps) => {
  const [isLoadingImage, setIsLoadingImage] = useState(true)
  const {
    areActionsDisabled,
    canAssignImage,
    canUploadImage,
    getInputProps,
    getRootProps,
    handleAssignImage,
    handleInputEvent,
    handleRemoveImage,
  } = useStyleColorProps(color)

  return (
    <StyleColorContainer
      data-testid={`${StyleColorsIds.Container}__${color.id}`}
    >
      <section>
        <SquaredSwatch
          hex={color?.swatch?.hex || ''}
          imageUrl={color?.swatch?.url || ''}
        />
        <Text
          fontWeight="medium"
          fontSize="15px"
          lineHeight="20"
          letterSpacing="wider"
        >
          {formatNameWithCode(color?.name, color?.code)}
        </Text>
      </section>
      {!!color?.imageUrl && (
        <ImageContainer>
          <ColorImage
            data-testid={`${StyleColorsIds.Image}__${color.id}`}
            onLoad={() => setIsLoadingImage(false)}
            src={getSizedImageUrl(
              encodeURIComponentImageName(color.imageUrl),
              GALLERY_IMAGE_DIMENSION,
            )}
          />
          {isLoadingImage && <Loader />}
          <RemoveIcon
            id={`${StyleColorsIds.RemoveButton}__${color.id}`}
            data-testid={`${StyleColorsIds.RemoveButton}__${color.id}`}
            {...(areActionsDisabled
              ? { className: 'disabled', variant: 'DISABLED' }
              : { variant: 'DEFAULT' })}
            iconName="close"
            iconSize="small"
            onClick={handleRemoveImage}
          />
        </ImageContainer>
      )}
      {!!canAssignImage && (
        <AppendImagePlaceholder
          id={`${StyleColorsIds.AssignImageButton}__${color.id}`}
          data-testid={`${StyleColorsIds.AssignImageButton}__${color.id}`}
          className={areActionsDisabled ? 'disabled' : ''}
          onClick={handleAssignImage}
          title="Assign"
        >
          <Icon iconName="add" />
        </AppendImagePlaceholder>
      )}
      {!!canUploadImage && (
        <span
          id={`${StyleColorsIds.UploadImageButton}__${color.id}`}
          data-testid={`${StyleColorsIds.UploadImageButton}__${color.id}`}
          {...getRootProps({
            onDrop: (e) => handleInputEvent(e),
            onClick: (e) => handleInputEvent(e),
          })}
        >
          <AppendImagePlaceholder
            className={areActionsDisabled ? 'disabled' : ''}
            title="Upload"
          >
            <Icon iconName="upload" />
          </AppendImagePlaceholder>
          <input {...getInputProps()} />
        </span>
      )}
    </StyleColorContainer>
  )
}

export default StyleColor
